/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Bar, Line } from 'react-chartjs-2';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
} from 'chart.js';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import GraphqlService from '../../service/graphqlService';
import { useProject } from '../../config/ProjectContext';
import DateRangePicker from '../../components/utils/DateRangePicker';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
);

const TotalUsersPerDayChart = ({ data, selectedStartDate, selectedEndDate }) => {
  if (!data || !data.totalUsersPerDay) {
    return null;
  }

  const { totalUsersPerDay } = data;

  const start = moment(selectedStartDate);
  const end = moment(selectedEndDate);
  const daysDifference = end.diff(start, 'days');

  const labels = [];
  if (daysDifference < 30) {
    const current = start.clone();
    while (current <= end) {
      if (current.day() !== 0 && current.day() !== 6) {
        labels.push(current.format('YYYY-MM-DD'));
      }
      current.add(1, 'day');
    }
  } else if (daysDifference <= 180) {
    const current = start.clone().startOf('isoWeek');
    while (current <= end) {
      labels.push(current.format('YYYY-[W]WW-YYYY'));
      current.add(1, 'week');
    }
  } else {
    const current = start.clone().startOf('month');
    while (current <= end) {
      labels.push(current.format('YYYY-MM'));
      current.add(1, 'month');
    }
  }

  const values = labels.map((label) => {
    const labelDate = moment(label, ['YYYY-MM-DD', 'YYYY-[W]WW-YYYY', 'YYYY-MM']);
    let totalValue = 0;
    let count = 0;

    Object.entries(totalUsersPerDay).forEach(([date, value]) => {
      const entryDate = moment(date);
      if (daysDifference < 30) {
        if (labelDate.isSame(entryDate, 'day')) {
          totalValue = value;
          count = 1;
        }
      } else if (daysDifference <= 180) {
        if (labelDate.isSame(entryDate, 'isoWeek') && labelDate.year() === entryDate.year()) {
          totalValue += value;
          count += 1;
        }
      } else if (labelDate.isSame(entryDate, 'month') && labelDate.year() === entryDate.year()) {
        totalValue += value;
        count += 1;
      }
    });

    return count > 0 ? totalValue / count : 0;
  });

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Average Users',
        data: values,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgb(75, 192, 192)',
        borderWidth: 1,
      },
      {
        type: 'line',
        label: 'Trend',
        data: values,
        borderColor: 'rgb(255, 99, 132)',
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: false,
      },
      y: {
        beginAtZero: true,
        stacked: false,
      },
    },
  };

  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const totalActionsData = Object.entries(totalUsersPerDay).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  const pageData = totalActionsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Calculates and lists the number of users per day.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="overflow-x-auto tailwind-scrollbar">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Users</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageData.map(([date, count]) => (
                <tr key={date}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="py-3 flex items-center justify-between">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page + 1} of {Math.ceil(totalActionsData.length / rowsPerPage)}
            </span>
            <button
              onClick={() => setPage(Math.min(page + 1, Math.ceil(totalActionsData.length / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(totalActionsData.length / rowsPerPage) - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <div>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

TotalUsersPerDayChart.propTypes = {
  data: PropTypes.object.isRequired,
  selectedStartDate: PropTypes.object.isRequired,
  selectedEndDate: PropTypes.object.isRequired,
};

const TotalActionsPerDayChart = ({ data, selectedStartDate, selectedEndDate }) => {
  if (!data || !data.totalActionsPerDay) {
    return null;
  }

  const { totalActionsPerDay } = data;
  const start = moment(selectedStartDate);
  const end = moment(selectedEndDate);
  const daysDifference = end.diff(start, 'days');

  const labels = [];
  if (daysDifference < 30) {
    const current = start.clone();
    while (current <= end) {
      if (current.day() !== 0 && current.day() !== 6) {
        labels.push(current.format('YYYY-MM-DD'));
      }
      current.add(1, 'day');
    }
  } else if (daysDifference <= 180) {
    const current = start.clone().startOf('isoWeek');
    while (current <= end) {
      labels.push(current.format('YYYY-[W]WW-YYYY'));
      current.add(1, 'week');
    }
  } else {
    const current = start.clone().startOf('month');
    while (current <= end) {
      labels.push(current.format('YYYY-MM'));
      current.add(1, 'month');
    }
  }

  const values = labels.map((label) => {
    const labelDate = moment(label, ['YYYY-MM-DD', 'YYYY-[W]WW-YYYY', 'YYYY-MM']);
    let totalValue = 0;
    let count = 0;

    Object.entries(totalActionsPerDay).forEach(([date, value]) => {
      const entryDate = moment(date);
      if (daysDifference < 30) {
        if (labelDate.isSame(entryDate, 'day')) {
          totalValue = value;
          count = 1;
        }
      } else if (daysDifference <= 180) {
        if (labelDate.isSame(entryDate, 'isoWeek') && labelDate.year() === entryDate.year()) {
          totalValue += value;
          count += 1;
        }
      } else if (labelDate.isSame(entryDate, 'month') && labelDate.year() === entryDate.year()) {
        totalValue += value;
        count += 1;
      }
    });
    return count > 0 ? totalValue / count : 0;
  });

  const chartData = {
    labels,
    datasets: [{
      label: 'Average Actions',
      data: values,
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1,
    }],
  };

  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: { stacked: false },
      y: { beginAtZero: true, stacked: false },
    },
  };

  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;
  const totalActionsData = Object.entries(totalActionsPerDay).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  const pageData = totalActionsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Calculates and lists the number of user actions that occurred on each day within a specified period.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="overflow-x-auto tailwind-scrollbar">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of User Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageData.map(([date, count]) => (
                <tr key={date}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="py-3 flex items-center justify-between">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page + 1} of {Math.ceil(totalActionsData.length / rowsPerPage)}
            </span>
            <button
              onClick={() => setPage(Math.min(page + 1, Math.ceil(totalActionsData.length / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(totalActionsData.length / rowsPerPage) - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <div>
          <Line data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

TotalActionsPerDayChart.propTypes = {
  data: PropTypes.object.isRequired,
  selectedStartDate: PropTypes.object.isRequired,
  selectedEndDate: PropTypes.object.isRequired,
};

const ActiveUsers = ({ data }) => {
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const sortedUsers = data.activeUsers.sort((a, b) => new Date(b.lastAction) - new Date(a.lastAction));
  const pageData = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Identifies and lists users who have engaged with the platform within the specified timeframe.
        </p>
      </div>
      <div className="overflow-x-auto tailwind-scrollbar">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Action</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pageData.map((user) => (
              <tr key={user.cognitoEmail}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.cognitoEmail}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.firstAction}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.lastAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="py-3 flex items-center justify-between">
        <button
          onClick={() => handleChangePage(Math.max(0, page - 1))}
          disabled={page === 0}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {page + 1} of {Math.ceil(sortedUsers.length / rowsPerPage)}
        </span>
        <button
          onClick={() => handleChangePage(Math.min(page + 1, Math.ceil(sortedUsers.length / rowsPerPage) - 1))}
          disabled={page >= Math.ceil(sortedUsers.length / rowsPerPage) - 1}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

ActiveUsers.propTypes = {
  data: PropTypes.object.isRequired,
};

const NewUsers = ({ data }) => {
  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const sortedNewUsers = data.newUsers.sort((a, b) => new Date(b.firstAction) - new Date(a.firstAction));
  const pageData = sortedNewUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Identifies users who have initiated their first action within a specified date range.
        </p>
      </div>
      <div className="overflow-x-auto tailwind-scrollbar">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pageData.map((user) => (
              <tr key={user.cognitoEmail}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.cognitoEmail}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.firstAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="py-3 flex items-center justify-between">
        <button
          onClick={() => handleChangePage(Math.max(0, page - 1))}
          disabled={page === 0}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-700">
          Page {page + 1} of {Math.ceil(data.newUsers.length / rowsPerPage)}
        </span>
        <button
          onClick={() => handleChangePage(Math.min(page + 1, Math.ceil(data.newUsers.length / rowsPerPage) - 1))}
          disabled={page >= Math.ceil(data.newUsers.length / rowsPerPage) - 1}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

NewUsers.propTypes = {
  data: PropTypes.object.isRequired,
};

const ActionsPerTypeChart = ({ data }) => {
  const actionTypes = {};
  data.actionPerDayPerType.forEach((day) => {
    Object.entries(day).forEach(([, actions]) => {
      Object.keys(actions).forEach((type) => {
        actionTypes[type] = (actionTypes[type] || 0) + Number(actions[type]);
      });
    });
  });
  const entries = Object.entries(actionTypes);
  entries.sort((a, b) => b[1] - a[1]);
  const actionTypesSorted = Object.fromEntries(entries);

  const chartData = {
    labels: Object.keys(actionTypesSorted),
    datasets: [{
      label: 'Total Actions Per Type',
      data: Object.values(actionTypesSorted),
      backgroundColor: 'rgba(255, 206, 86, 0.6)',
    }],
  };

  const options = {
    responsive: true,
    scales: { y: { beginAtZero: true } },
  };

  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const actionsTypeArray = Object.entries(actionTypesSorted);
  const pageData = actionsTypeArray.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Aggregates and displays the number of actions performed, categorized by type, over a specified period.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="overflow-x-auto tailwind-scrollbar">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageData.map(([type, count]) => (
                <tr key={type}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{type}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="py-3 flex items-center justify-between">
            <button
              onClick={() => handleChangePage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page + 1} of {Math.ceil(actionsTypeArray.length / rowsPerPage)}
            </span>
            <button
              onClick={() => handleChangePage(Math.min(page + 1, Math.ceil(actionsTypeArray.length / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(actionsTypeArray.length / rowsPerPage) - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <div>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

ActionsPerTypeChart.propTypes = {
  data: PropTypes.object.isRequired,
};

const ActionsSectionsPerTypeChart = ({ data }) => {
  const actionTypes = { TELEMETRY: 0, FOTA: 0, DEV_TOOLS: 0 };
  data.actionPerDayPerType.forEach((day) => {
    Object.entries(day).forEach(([, actions]) => {
      Object.keys(actions).forEach((type) => {
        const count = Number(actions[type]);
        const typeLower = String(type).toLowerCase();
        if (typeLower.includes('asset') || typeLower.includes('deploy') || typeLower.includes('lifecycle')) {
          actionTypes.FOTA += count;
        } else if (typeLower.includes('executesrf') || typeLower.includes('onboardingdevice') || typeLower.includes('subscribetotopic')) {
          actionTypes.DEV_TOOLS += count;
        } else {
          actionTypes.TELEMETRY += count;
        }
      });
    });
  });

  const chartData = {
    labels: Object.keys(actionTypes),
    datasets: [{
      label: 'Total Actions Per Service',
      data: Object.values(actionTypes),
      backgroundColor: 'rgba(255, 206, 86, 0.6)',
    }],
  };

  const options = {
    responsive: true,
    scales: { y: { beginAtZero: true } },
  };

  const [page, setPage] = React.useState(0);
  const rowsPerPage = 5;

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const actionsTypeArray = Object.entries(actionTypes);
  const pageData = actionsTypeArray.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Aggregates and displays the number of actions performed, categorized by section, over a specified period.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="overflow-x-auto tailwind-scrollbar">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action per Service</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageData.map(([category, count]) => (
                <tr key={category}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="py-3 flex items-center justify-between">
            <button
              onClick={() => handleChangePage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page + 1} of {Math.ceil(actionsTypeArray.length / rowsPerPage)}
            </span>
            <button
              onClick={() => handleChangePage(Math.min(page + 1, Math.ceil(actionsTypeArray.length / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(actionsTypeArray.length / rowsPerPage) - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <div>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

ActionsSectionsPerTypeChart.propTypes = {
  data: PropTypes.object.isRequired,
};

const ActionsPerDayPerTypeChart = ({ data }) => {
  const dates = data.actionPerDayPerType.map((item) => Object.keys(item)[0]);
  const actionTypesSet = new Set();

  data.actionPerDayPerType.forEach((day) => {
    const actions = Object.values(day)[0];
    Object.keys(actions).forEach((type) => actionTypesSet.add(type));
  });

  const actionTypes = Array.from(actionTypesSet);
  const sortedDates = [...dates].sort((a, b) => new Date(a) - new Date(b));

  const chartDataSets = actionTypes.map((type) => ({
    label: type,
    data: Array(sortedDates.length).fill(0),
    backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, 0.6)`,
  }));

  sortedDates.forEach((date, index) => {
    const day = data.actionPerDayPerType.find((item) => Object.keys(item)[0] === date);
    const actions = day[date];
    actionTypes.forEach((type, typeIndex) => {
      chartDataSets[typeIndex].data[index] = actions[type] || 0;
    });
  });

  const chartData = {
    labels: sortedDates,
    datasets: chartDataSets,
  };

  const options = {
    responsive: true,
    scales: {
      x: { stacked: true },
      y: { stacked: true },
    },
  };

  const [page, setPage] = React.useState(0);
  const rowsPerPage = 3;

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const sortedTableData = [...data.actionPerDayPerType].sort((a, b) => new Date(Object.keys(b)[0]) - new Date(Object.keys(a)[0]));
  const pageData = sortedTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="mt-4">
      <div className="bg-blue-100 p-3 rounded-lg my-2">
        <p className="text-xs text-gray-700">
          Tracks and reports the number of specific types of actions that occur each day within a set period.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="overflow-x-auto tailwind-scrollbar">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pageData.map((item) => (
                <tr key={Object.keys(item)[0]}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{Object.keys(item)[0]}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Object.entries(item[Object.keys(item)[0]]).reduce((acc, [type, count]) => `${acc}${type}: ${count}, `, '').slice(0, -2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="py-3 flex items-center justify-between">
            <button
              onClick={() => handleChangePage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {page + 1} of {Math.ceil(sortedTableData.length / rowsPerPage)}
            </span>
            <button
              onClick={() => handleChangePage(Math.min(page + 1, Math.ceil(sortedTableData.length / rowsPerPage) - 1))}
              disabled={page >= Math.ceil(sortedTableData.length / rowsPerPage) - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        <div>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

ActionsPerDayPerTypeChart.propTypes = {
  data: PropTypes.object.isRequired,
};

export default function KPIsCharts() {
  const { project } = useProject();

  const initialGlobalStartMoment = moment().subtract(1, 'days');
  const initialGlobalEndMoment = moment();

  const [selectedStartDate, setSelectedStartDate] = useState(initialGlobalStartMoment);
  const [selectedEndDate, setSelectedEndDate] = useState(initialGlobalEndMoment);
  const [selectedStartTime, setSelectedStartTime] = useState(initialGlobalStartMoment.format('HH:mm'));
  const [selectedEndTime, setSelectedEndTime] = useState(initialGlobalEndMoment.format('HH:mm'));

  const [selectedFormattedStartDate, setSelectedFormattedStartDate] = useState(initialGlobalStartMoment.format('YYYY-MM-DDTHH:mm:ssZ'));
  const [selectedFormattedEndDate, setSelectedFormattedEndDate] = useState(initialGlobalEndMoment.format('YYYY-MM-DDTHH:mm:ssZ'));

  const [searchButton, setSearchButton] = useState(false);
  const [seeAllProjects, setSeeAllProjects] = useState(false);

  const getActionStatistics = async () => GraphqlService.getActionStatistics(selectedFormattedStartDate, selectedFormattedEndDate, seeAllProjects ? undefined : project?.code);

  useEffect(() => {
    if (selectedStartDate) {
      setSelectedFormattedStartDate(moment(selectedStartDate).format('YYYY-MM-DDTHH:mm:ssZ'));
    } else {
      setSelectedFormattedStartDate(null);
    }
  }, [selectedStartDate]);

  useEffect(() => {
    if (selectedEndDate) {
      setSelectedFormattedEndDate(moment(selectedEndDate).format('YYYY-MM-DDTHH:mm:ssZ'));
    } else {
      setSelectedFormattedEndDate(null);
    }
  }, [selectedEndDate]);

  const {
    data,
    error,
    isLoading,
  } = useQuery(['kpis', searchButton, seeAllProjects, project?.code, selectedFormattedStartDate, selectedFormattedEndDate], () => getActionStatistics(), {
    cacheTime: 0,
    select: (res) => JSON.parse(res),
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center p-10">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Retrieving KPIs users, please wait...</p>
        </div>
      );
    }
    if (error) {
      return (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error.message}</span>
        </div>
      );
    }

    return (
      <div className="bg-gray-50 p-4">
        {data && (
          <div className="space-y-12">
            <div>
              <h2 className="text-xl text-gray-800 mb-2">
                Active Users ({data?.activeUsers?.length})
              </h2>
              <ActiveUsers data={data} />
            </div>
            <div>
              <h2 className="text-xl text-gray-800 mb-2">
                Total Users ({data?.totalCognitoUsers})
              </h2>
              <TotalUsersPerDayChart
                data={data}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
              />
            </div>
            <div>
              <h2 className="text-xl text-gray-800 mb-2">
                Total Actions
              </h2>
              <TotalActionsPerDayChart
                data={data}
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
              />
            </div>
            <div>
              <h2 className="text-xl text-gray-800 mb-2">
                Actions Per Type
              </h2>
              <ActionsPerTypeChart data={data} />
            </div>
            <div>
              <h2 className="text-xl text-gray-800 mb-2">
                Services
              </h2>
              <ActionsSectionsPerTypeChart data={data} />
            </div>
            <div>
              <h2 className="text-xl text-gray-800 mb-2">
                Actions Per Day Per Type
              </h2>
              <ActionsPerDayPerTypeChart data={data} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-[#F5936B] px-4 py-3">
        <h1 className="text-xl text-white ">KPIS</h1>
      </div>

      <div className="bg-white p-4 md:p-6">
        <div className="flex flex-wrap gap-4 items-center mb-4">
          <DateRangePicker
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedStartTime={selectedStartTime}
            setSelectedStartTime={setSelectedStartTime}
            selectedEndTime={selectedEndTime}
            setSelectedEndTime={setSelectedEndTime}
            onDateChange={() => { }}
            onApply={() => setSearchButton(prev => !prev)}
            datesOptional={false}
          />

          <button
            id="btnSearch"
            onClick={() => setSearchButton(!searchButton)}
            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <MagnifyingGlassIcon className="h-5 w-5 mr-2" /> Search
          </button>
        </div>
        <div className="flex items-center">
          <label htmlFor="seeAllProjectsToggle" className="flex items-center cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                id="seeAllProjectsToggle"
                className="sr-only"
                checked={seeAllProjects}
                onChange={(e) => setSeeAllProjects(e.target.checked)}
              />
              <div
                className={`block w-10 h-6 rounded-full ${seeAllProjects ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
              ></div>
              <div
                className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${seeAllProjects ? 'transform translate-x-full' : ''
                  }`}
              ></div>
            </div>
            <div className="ml-3 text-sm font-medium text-gray-700">
              See all projects statistics
            </div>
          </label>
        </div>
      </div>

      {renderContent()}
    </div>
  );
}
